import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { StepFooter } from '../StepFooter';
import { ApiError, productApi } from '../../../api';

/**
 * Setup step 2 — Academic session, terms, holidays. Real forms; persists to
 * academic_session / term / holiday tables. Terms and holidays require a
 * current session — if none exists, only the session card is shown.
 */
export function CalendarSetupPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [openSession, setOpenSession] = useState(false);
  const [openTerm, setOpenTerm] = useState(false);
  const [openHoliday, setOpenHoliday] = useState(false);

  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution,
  });
  const current = cal.data?.current_session ?? null;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['calendar', institution] });

  const delSession = useMutation({
    mutationFn: (id: string) => productApi.deleteSession(institution, id),
    onSuccess: () => { invalidate(); void message.success('Session removed'); },
    onError: (e) => void message.error(errMsg(e) ?? 'Delete failed'),
  });
  const setCur = useMutation({
    mutationFn: (id: string) => productApi.setSessionCurrent(institution, id),
    onSuccess: () => { invalidate(); void message.success('Set as current'); },
    onError: (e) => void message.error(errMsg(e) ?? 'Failed'),
  });
  const delTerm = useMutation({
    mutationFn: (id: string) => productApi.deleteTerm(institution, id),
    onSuccess: () => { invalidate(); void message.success('Term removed'); },
    onError: (e) => void message.error(errMsg(e) ?? 'Delete failed'),
  });
  const delHoliday = useMutation({
    mutationFn: (id: string) => productApi.deleteHoliday(institution, id),
    onSuccess: () => { invalidate(); void message.success('Holiday removed'); },
    onError: (e) => void message.error(errMsg(e) ?? 'Delete failed'),
  });

  const canMarkDone = !!current;

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Year and calendar
        </Typography.Title>
        <Typography.Text type="secondary">
          Every date in the system reads from here — an exam cannot be scheduled on a holiday
          because the datesheet asks this one.
        </Typography.Text>
      </div>

      <Card
        size="small"
        title="Academic sessions"
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpenSession(true)}>
            Add session
          </Button>
        }
        style={{ marginBottom: 14 }}
      >
        {cal.data && cal.data.sessions.length === 0 ? (
          <Empty
            description={
              <span>
                No sessions yet. <a onClick={() => setOpenSession(true)}>Add the first one</a>.
              </span>
            }
            style={{ padding: 16 }}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={cal.data?.sessions ?? []}
            columns={[
              {
                title: 'Label',
                dataIndex: 'label',
                key: 'label',
                render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span>,
              },
              { title: 'Starts', dataIndex: 'starts_on', key: 'starts_on' },
              { title: 'Ends', dataIndex: 'ends_on', key: 'ends_on' },
              {
                title: 'Current',
                dataIndex: 'is_current',
                key: 'is_current',
                render: (v: boolean) => (v ? <Tag color="success">current</Tag> : null),
              },
              {
                key: 'action',
                width: 160,
                align: 'right',
                render: (_, r) => (
                  <Space size={4}>
                    {!r.is_current && (
                      <Button size="small" onClick={() => setCur.mutate(r.id)}>Set current</Button>
                    )}
                    <Popconfirm
                      title="Delete this session?"
                      description="Only if it has no terms or holidays."
                      onConfirm={() => delSession.mutate(r.id)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                    >
                      <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      {current && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Card
            size="small"
            title={`Terms in ${current.label}`}
            extra={
              <Button size="small" onClick={() => setOpenTerm(true)} icon={<PlusOutlined />}>
                Add term
              </Button>
            }
          >
            {(cal.data?.terms.filter((t) => t.session_id === current.id) ?? []).length === 0 ? (
              <Empty style={{ padding: 8 }} description="No terms yet" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={cal.data?.terms.filter((t) => t.session_id === current.id) ?? []}
                columns={[
                  { title: 'Term', dataIndex: 'label', key: 'label' },
                  { title: 'Starts', dataIndex: 'starts_on', key: 'starts_on' },
                  { title: 'Ends', dataIndex: 'ends_on', key: 'ends_on' },
                  {
                    key: 'action', width: 40, align: 'right',
                    render: (_, r) => (
                      <Popconfirm title="Delete this term?" onConfirm={() => delTerm.mutate(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
                        <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          <Card
            size="small"
            title={`Holidays in ${current.label}`}
            extra={
              <Button size="small" onClick={() => setOpenHoliday(true)} icon={<PlusOutlined />}>
                Add holiday
              </Button>
            }
          >
            {(cal.data?.holidays.filter((h) => h.session_id === current.id) ?? []).length === 0 ? (
              <Empty style={{ padding: 8 }} description="No holidays yet" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={cal.data?.holidays.filter((h) => h.session_id === current.id) ?? []}
                columns={[
                  { title: 'Date', dataIndex: 'date', key: 'date' },
                  { title: 'Label', dataIndex: 'label', key: 'label' },
                  {
                    key: 'action', width: 40, align: 'right',
                    render: (_, r) => (
                      <Popconfirm title="Delete this holiday?" onConfirm={() => delHoliday.mutate(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
                        <Button size="small" type="text" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </div>
      )}

      {!canMarkDone && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message="Add a session and mark it current before finishing this step."
        />
      )}

      <StepFooter
        institution={institution}
        step="calendar"
        summary={current ? `Session ${current.label} active` : undefined}
        disabled={!canMarkDone}
        disabledReason="Add and set a current session first"
        nextHref={`/i/${institution}/setup/staff`}
        nextLabel="Next: staff →"
      />

      <SessionModal open={openSession} onClose={() => setOpenSession(false)} institution={institution} onCreated={() => { invalidate(); setOpenSession(false); void message.success('Session added'); }} />
      {current && (
        <>
          <TermModal open={openTerm} onClose={() => setOpenTerm(false)} institution={institution} sessionId={current.id} onCreated={() => { invalidate(); setOpenTerm(false); void message.success('Term added'); }} />
          <HolidayModal open={openHoliday} onClose={() => setOpenHoliday(false)} institution={institution} sessionId={current.id} onCreated={() => { invalidate(); setOpenHoliday(false); void message.success('Holiday added'); }} />
        </>
      )}
    </div>
  );
}

function SessionModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ label: string; starts_on: string; ends_on: string; is_current: 'yes' | 'no' }>();
  const m = useMutation({
    mutationFn: (v: { label: string; starts_on: string; ends_on: string; is_current: boolean }) =>
      productApi.createSession(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal
      open={open} title="Add academic session" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ is_current: 'yes' }} onFinish={(v) => m.mutate({ ...v, is_current: v.is_current === 'yes' })}>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="2026-27" autoFocus /></Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="starts_on" label="Starts on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}><Input placeholder="2026-04-01" /></Form.Item>
          <Form.Item name="ends_on" label="Ends on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}><Input placeholder="2027-03-31" /></Form.Item>
        </div>
        <Form.Item name="is_current" label="Set as current?">
          <Select options={[{ value: 'yes', label: 'Yes — this is the running year' }, { value: 'no', label: 'No — historic or upcoming' }]} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function TermModal({
  open, onClose, institution, sessionId, onCreated,
}: { open: boolean; onClose: () => void; institution: string; sessionId: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ label: string; starts_on: string; ends_on: string }>();
  const m = useMutation({
    mutationFn: (v: { label: string; starts_on: string; ends_on: string }) =>
      productApi.createTerm(institution, { session_id: sessionId, ...v }),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add term" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)}>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="Term 1" autoFocus /></Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="starts_on" label="Starts on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}><Input placeholder="2026-04-01" /></Form.Item>
          <Form.Item name="ends_on" label="Ends on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}><Input placeholder="2026-09-30" /></Form.Item>
        </div>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function HolidayModal({
  open, onClose, institution, sessionId, onCreated,
}: { open: boolean; onClose: () => void; institution: string; sessionId: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ date: string; label: string }>();
  const m = useMutation({
    mutationFn: (v: { date: string; label: string }) => productApi.createHoliday(institution, { session_id: sessionId, ...v }),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add holiday" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate(v)}>
        <Form.Item name="date" label="Date" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}><Input placeholder="2026-08-15" autoFocus /></Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="Independence Day" /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
